import React, { useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { cn } from '../../utils/cn';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { getHTML } from '@/utils/md';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

// 浏览器环境检测
const isBrowser = typeof window !== 'undefined';

if (isBrowser) {
  require('codemirror/mode/htmlmixed/htmlmixed.js');
  require('codemirror/lib/codemirror.css');
  require('codemirror/keymap/sublime.js');
  require('codemirror/theme/solarized.css');
}

// 验证 URL 是否为有效的外部 HTTP/HTTPS URL
const isValidExternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') && 
           !urlObj.toString().includes('webpack-internal:') &&
           !urlObj.pathname.endsWith('.tsx') &&
           !urlObj.pathname.endsWith('.jsx') &&
           !urlObj.pathname.endsWith('.ts') &&
           !urlObj.pathname.endsWith('.js');
  } catch (e) {
    return false;
  }
};

interface HTMLEditorProps {
  code: string;
  onCodeChange: (newCode: string) => void;
  urlPlaceholder?: string;
  invalidUrlMessage?: string;
  urlParseSuccess?: string;
  urlParseError?: string;
  urlParsing?: string;
  batchToolMessage?: string;
}

export const HTMLEditor: React.FC<HTMLEditorProps> = ({
  code,
  onCodeChange,
  urlPlaceholder = "请输入网页地址",
  invalidUrlMessage = "请输入有效的 URL，需以 http:// 或 https:// 开头",
  urlParseSuccess = "解析成功",
  urlParseError = "解析失败",
  urlParsing = "正在解析...",
  batchToolMessage = "需要批量转换多个 URL？试试我们的批量 URL 转 Markdown 工具"
}) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { locale } = useRouter();

  const editorOptions = {
    mode: 'htmlmixed',
    theme: 'solarized light',
    tabSize: 2,
    keyMap: 'sublime',
    autoFocus: true,
    autoCloseBrackets: true,
    matchBrackets: true,
    rulerColor: '#000',
    viewportMargin: Infinity,
    lineWrapping: true
  };

  const handleUrlSubmit = async () => {
    if (!isValidExternalUrl(url)) {
      toast.error(invalidUrlMessage);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getHTML(url);
      if (result?.content) {
        onCodeChange(result.content);
        toast.success(urlParseSuccess);
      } else {
        toast.error(urlParseError);
      }
    } catch (error) {
      console.error('Error fetching URL:', error);
      toast.error(urlParseError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center space-x-2">
          <Input
            type="url"
            value={url}
            className="flex-grow"
            placeholder={urlPlaceholder}
            spellCheck={false}
            pattern="https://.*"
            disabled={isLoading}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleUrlKeyDown}
          />
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleUrlSubmit}
            disabled={isLoading}
          >
            {isLoading ? urlParsing : "获取"}
          </Button>
        </div>
      </div>

      <div className="flex-grow overflow-hidden relative">
        {isBrowser && (
          <CodeMirror
            value={code}
            options={editorOptions}
            onBeforeChange={(editor, data, value) => {
              onCodeChange(value);
            }}
            className="absolute inset-0 h-full"
          />
        )}
      </div>
    </div>
  );
}; 